// Event Form Component
// BP-10-04: Attorney Frontend

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2 } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { useCreateEvent, useUpdateEvent } from '@/features/attorney/hooks/useCaseEvents';
import type { CaseEvent, CaseEventFormData, CaseEventType, EventPriority } from '@/features/attorney/types/attorney';
import { EVENT_TYPE_OPTIONS, PRIORITY_OPTIONS } from '@/features/attorney/types/attorney';

const eventFormSchema = z.object({
  eventType: z.enum([
    'filing',
    'hearing',
    'deposition',
    'mediation',
    'motion',
    'document',
    'note',
    'milestone',
  ]),
  title: z.string().min(1, 'Title is required').max(300),
  description: z.string().max(5000).optional(),
  eventDate: z.string().optional(),
  dueDate: z.string().optional(),
  visibleToClient: z.boolean(),
  priority: z.enum(['high', 'medium', 'low']),
});

type EventFormValues = z.infer<typeof eventFormSchema>;

interface EventFormProps {
  caseId: string;
  event?: CaseEvent;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function EventForm({ caseId, event, onSuccess, onCancel }: EventFormProps) {
  const isEditing = !!event;

  const createEvent = useCreateEvent(caseId);
  const updateEvent = useUpdateEvent(caseId);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: event
      ? {
          eventType: event.eventType,
          title: event.title,
          description: event.description || '',
          eventDate: event.eventDate?.split('T')[0] || '',
          dueDate: event.dueDate?.split('T')[0] || '',
          visibleToClient: event.visibleToClient,
          priority: event.priority,
        }
      : {
          eventType: 'note' as CaseEventType,
          title: '',
          description: '',
          eventDate: '',
          dueDate: '',
          visibleToClient: false,
          priority: 'medium' as EventPriority,
        },
  });

  const watchEventType = watch('eventType');

  const onSubmit = async (data: EventFormValues) => {
    const formData: CaseEventFormData = {
      eventType: data.eventType as CaseEventType,
      title: data.title,
      description: data.description,
      eventDate: data.eventDate || undefined,
      dueDate: data.dueDate || undefined,
      visibleToClient: data.visibleToClient,
      priority: data.priority as EventPriority,
    };

    try {
      if (isEditing && event) {
        await updateEvent.mutateAsync({ eventId: event.id, data: formData });
      } else {
        await createEvent.mutateAsync(formData);
      }
      onSuccess?.();
    } catch (error) {
      console.error('Failed to save event:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-navy">
          {isEditing ? 'Edit Event' : 'Add Event'}
        </h3>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Event Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Event Type
          </label>
          <select
            {...register('eventType')}
            className={cn(
              'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
              'focus:outline-none focus:border-sage transition-colors',
              errors.eventType && 'border-red-300'
            )}
          >
            {EVENT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.eventType && (
            <p className="text-red-500 text-sm mt-1">{errors.eventType.message}</p>
          )}
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority
          </label>
          <select
            {...register('priority')}
            className={cn(
              'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
              'focus:outline-none focus:border-sage transition-colors'
            )}
          >
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          {...register('title')}
          type="text"
          placeholder="Enter event title"
          className={cn(
            'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
            'focus:outline-none focus:border-sage transition-colors',
            errors.title && 'border-red-300'
          )}
        />
        {errors.title && (
          <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          {...register('description')}
          rows={3}
          placeholder="Add details about this event..."
          className={cn(
            'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
            'focus:outline-none focus:border-sage transition-colors resize-none'
          )}
        />
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Event Date
          </label>
          <input
            {...register('eventDate')}
            type="date"
            className={cn(
              'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
              'focus:outline-none focus:border-sage transition-colors'
            )}
          />
          <p className="text-xs text-gray-500 mt-1">
            The date when this event occurred/occurs
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Due Date
          </label>
          <input
            {...register('dueDate')}
            type="date"
            className={cn(
              'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
              'focus:outline-none focus:border-sage transition-colors'
            )}
          />
          <p className="text-xs text-gray-500 mt-1">
            Set a deadline for tasks/deadlines
          </p>
        </div>
      </div>

      {/* Visibility Toggle */}
      <div className="flex items-center gap-3">
        <input
          {...register('visibleToClient')}
          type="checkbox"
          id="visibleToClient"
          className="w-4 h-4 rounded border-gray-300 text-sage focus:ring-sage"
        />
        <label htmlFor="visibleToClient" className="text-sm text-gray-700">
          Make visible to client in their portal
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            'px-6 py-2 bg-sage text-white rounded-lg font-medium',
            'hover:bg-sage-dark transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'flex items-center gap-2'
          )}
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {isEditing ? 'Update Event' : 'Add Event'}
        </button>
      </div>
    </form>
  );
}

export default EventForm;

